import React from 'react'
import { Icon, type TIconProps } from '~/components/icon'
import { RadioHelperText } from './components/helper-text'
import { RadioLabel } from './components/radio-label'
import { useRadioVariants } from './hooks'
import * as Comp from './radio.styles'
import type { TRadioProps } from './types'

const RadioRoot = ({
  size = 'md',
  checked = false,
  onPress,
  error,
  disabled = false,
  readOnly = false,
  children,
  testID,
}: TRadioProps) => {
  const { iconSizeMapping, renderCurrentStateRadio, pressed, setPressed } =
    useRadioVariants()

  const renderInternalLabel = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return

    if (React.isValidElement(child) && child.type === RadioLabel) {
      return React.cloneElement(child as React.ReactElement<TRadioProps>, {
        size,
        disabled,
        readOnly,
        error,
      })
    }
  })

  const renderInternalHelperText = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return

    if (React.isValidElement(child) && child.type === RadioHelperText) {
      return React.cloneElement(child as React.ReactElement<TRadioProps>, {
        size,
      })
    }
  })

  const renderNestedParent = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return

    if (child.type === Comp.NestedContainer) {
      return React.cloneElement(child as React.ReactElement<TRadioProps>, {
        testID: 'radio-nested-container',
      })
    }
  })

  const componentProperties = {
    size,
    hasPressed: pressed,
    hasDisabled: disabled,
    hasReadOnly: readOnly,
    hasError: !!error,
    hasInternalLabel: !!renderInternalLabel?.length,
  }

  const iconColorMapping = (): TIconProps['color'] => {
    return disabled
      ? 'colorTextNeutralDisabled'
      : readOnly
        ? 'colorTextNeutralReadonly'
        : 'colorTextNeutralDefault'
  }

  return (
    <Comp.Root
      testID={testID}
      disabled={disabled || readOnly}
      onPress={() => {
        if (typeof onPress === 'function') {
          onPress()
        }
      }}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      {...componentProperties}
    >
      <Comp.Actions {...componentProperties}>
        <Icon
          testID="radio-icon"
          color={iconColorMapping()}
          size={iconSizeMapping[size]}
          symbol={renderCurrentStateRadio(checked)}
        />
        {renderInternalLabel}
      </Comp.Actions>
      {error && !!renderInternalLabel?.length ? renderInternalHelperText : null}
      {renderNestedParent}
    </Comp.Root>
  )
}

export const Radio = {
  Root: RadioRoot,
  Label: RadioLabel,
  HelperText: RadioHelperText,
  NestedContainer: Comp.NestedContainer,
}

export type { TRadioProps }
